import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../util/api";
import { useLocation } from "react-router-dom";

import SearchBar from "./SearchBar";
import BidDetailModal from "./BidDetailModal";
import FavoriteStar from "./components/FavoriteStar";
import { favoriteApi } from "./api/favoriteApi";

// MUI
import {
    Box,
    Typography,
    Stack,
    Divider,
    Backdrop,
    CircularProgress,
    Button,
    FormControlLabel,
    Checkbox,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableSortLabel,
    Card,
    CardContent,
    Chip,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import { ds } from "../../styles/designSystem";
import LoadingDots from "../../component/common/LoadingDots";
import ActionButton from "../../component/common/ActionButton";
import ResizableTable from "../../component/common/ResizableTable";

// Charts (Recharts)
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip as ReTooltip,
} from "recharts";

const useQuery = () => new URLSearchParams(useLocation().search);

const formatWon = (n) =>
    Number.isFinite(Number(n)) ? `${Number(n).toLocaleString()}원` : "-";
const formatCount = (n) =>
    Number.isFinite(Number(n)) ? `${Number(n).toLocaleString()}건` : "-";
const formatPercent = (n) =>
    Number.isFinite(Number(n))
        ? `${Number(n).toLocaleString("ko-KR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
        : "-";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const cardSx = {
    ...ds.components.external.card,
};

const sectionTitleSx = { ...ds.typography.sectionTitle, fontSize: 18 };

// ✅ 색상 (요청 반영)
const COLORS = {
    winRate: "#22c55e", // 낙찰률(초록)
    winAmount: "#3182f6", // 낙찰금액(파랑)

    // 다양하게
    winCount: "#3b82f6", // 낙찰 건수
    totalBid: "#06b6d4", // 투찰(민트)
    muted: ds.colors.border,
};

// ✅ 커스텀 Tooltip: 위치 튐 방지 + 디자인 정리
function CustomTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;

    const p = payload[0];
    return (
        <Box
            sx={{
                bgcolor: ds.colors.surface,
                border: `1px solid ${ds.colors.border}`,
                boxShadow: "0 12px 30px rgba(15, 23, 42, 0.12)",
                borderRadius: 1.5,
                px: 1.25,
                py: 0.75,
            }}
            width={100}
        >
            <Typography sx={{ fontSize: 12, fontWeight: 900, mb: 0.25 }}>
                {p.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {p.valueText ?? p.value?.toLocaleString()}
            </Typography>
        </Box>
    );
}

function DonutCard({
                                      title,
                                      subtitle,
                                      mainLabel,
                                      mainValueText,
                                      data,
                                      colors,
                                  }) {
    return (
        <Box
            sx={{
                ...ds.components.external.card,
                p: 2,
                height: 300,            // ✅ 높이 고정 (차트 계산 안정화)
                overflow: "hidden",      // ✅ 밖으로 나가는 것 컷
            }}
        >
            <Stack spacing={0.25} sx={{ mb: 1 }}>
                <Typography sx={ds.typography.sectionTitle}>{title}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {subtitle}
                </Typography>
            </Stack>

            <Box sx={{ position: "relative", height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            // ✅ 중앙 고정 (튀는 현상 방지)
                            // ✅ 반지름 축소해서 카드 밖으로 안 나가게
                            innerRadius={80}
                            outerRadius={110}
                            startAngle={90}
                            endAngle={-270}
                            paddingAngle={2}
                            isAnimationActive={true} // ✅ 툴팁/레이아웃 튐 줄이기
                        >
                            {data.map((_, idx) => (
                                <Cell key={idx} fill={colors[idx]} />
                            ))}
                        </Pie>

                        <ReTooltip
                            content={<CustomTooltip />}
                            // ✅ 툴팁 튐 방지 옵션
                            wrapperStyle={{ pointerEvents: "none" }}
                            position={{ x: 12, y: 12 }} // ✅ 카드 내부 좌상단 고정(가장 안정적)
                            allowEscapeViewBox={{ x: false, y: false }}
                            isAnimationActive={false}
                        />
                    </PieChart>
                </ResponsiveContainer>

                {/* center label */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                        textAlign: "center"
                    }}
                >

                    <Typography variant="caption" color="text.secondary">
                        {mainLabel}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 16, letterSpacing: "-0.02em" }}>
                        {mainValueText}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

function MiniStat({ title, value, accent }) {
    return (
        <Box
            sx={{
                ...ds.components.external.card,
                p: 1.5,
                textAlign: "left",
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {title}
            </Typography>
            <Typography
                sx={{
                    mt: 0.5,
                    fontWeight: 800,
                    fontSize: 18,
                    letterSpacing: "-0.02em",
                    color: accent || "text.primary",
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

const CompanySearchPage = () => {
    const query = useQuery();
    const bizNumber = query.get("bizNumber");

    const [company, setCompany] = useState(null);

    // 즐겨찾기 — 회사가 set 되면 별 상태 조회. 토글은 사업자번호 기준.
    const [companyFav, setCompanyFav] = useState(false);
    useEffect(() => {
        const biz = company?.businessNumber;
        if (!biz) { setCompanyFav(false); return; }
        let cancelled = false;
        (async () => {
            try {
                const favs = await favoriteApi.filterCompanyBiznos([biz]);
                if (!cancelled) setCompanyFav(Array.isArray(favs) ? favs.includes(biz) : !!favs?.[biz]);
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, [company?.businessNumber]);
    const toggleCompanyFav = async (next) => {
        const biz = company?.businessNumber;
        if (!biz) return;
        setCompanyFav(next);   // 낙관적
        try {
            if (next) await favoriteApi.addCompany(biz);
            else      await favoriteApi.removeCompany(biz);
        } catch { setCompanyFav(!next); /* 롤백 */ }
    };
    const [stats, setStats] = useState(null);
    const [progress, setProgress] = useState(null);
    const [selected, setSelected] = useState(null);
    const [showOnlyWon, setShowOnlyWon] = useState(false);

    // 정렬
    const [sortKey, setSortKey] = useState("bidOpenDate");
    const [sortAsc, setSortAsc] = useState(false);

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (bizNumber) handleSearch(bizNumber);
        // eslint-disable-next-line
    }, [bizNumber]);

    const handleSearch = async (businessNumber) => {
        setLoading(true);
        setStats(null);
        setCompany(null);
        setProgress(null);

        try {
            // 1) 통계 + 목록
            const res = await api.get(
                `${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results`,
                { bizNumber: businessNumber }
            );

            if (res?.working || res?.status === 202) {
                const token = localStorage.getItem("auth_token");

                const es = new EventSource(
                    `${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results/stream?bizNumber=${encodeURIComponent(
                        businessNumber
                    )}&token=${encodeURIComponent(token || "")}`
                );

                es.addEventListener("progress", (e) => {
                    try {
                        setProgress(JSON.parse(e.data));
                    } catch {
                        // ignore
                    }
                });

                es.addEventListener("done", async () => {
                    es.close();
                    setProgress(null);

                    const statsRes = await api.get(
                        `${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results`,
                        { bizNumber: businessNumber }
                    );
                    setStats(statsRes);
                });

                es.onerror = () => es.close();
            } else if (res?.allResults) {
                setStats(res);
            } else {
                setStats(null);
            }

            // 2) 회사 정보
            const companyInfo = await api.get(
                `${process.env.REACT_APP_API_BASE_URL}/companies/info`,
                { bizNumber: businessNumber }
            );
            setCompany(companyInfo);

            setLoading(false);
        } catch (err) {
            console.error("❌ 데이터 조회 실패:", err);
            setLoading(false);
            setStats(null);
            setCompany(null);
            setProgress(null);
        }
    };

    const handleSort = (key) => {
        if (sortKey === key) setSortAsc((v) => !v);
        else {
            setSortKey(key);
            setSortAsc(true);
        }
    };

    const displayResults = useMemo(() => {
        const base = showOnlyWon ? stats?.wonResults || [] : stats?.allResults || [];
        const arr = [...base];

        arr.sort((a, b) => {
            const av = a?.[sortKey];
            const bv = b?.[sortKey];
            if (av == null || bv == null) return 0;

            if (typeof av === "string" && typeof bv === "string") {
                return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
            }
            return sortAsc ? Number(av) - Number(bv) : Number(bv) - Number(av);
        });

        return arr;
    }, [stats, showOnlyWon, sortKey, sortAsc]);

    const handleDownload = async () => {
        if (!bizNumber) return;
        setLoading(true);

        try {
            // api.download 는 axios 응답 객체를 준다 (http.js:19). .data 를 벗겨야 실제 Blob.
            const res  = await api.download(
                `${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results/export`,
                { bizNumber }
            );
            const blob = res?.data ?? res;

            // 래핑은 유지 — 서버가 octet-stream 으로 내려주는 것을 xlsx MIME 으로 바꿔 붙이는 의도.
            const blobObj = new Blob([blob], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blobObj);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `bidding-results-${bizNumber}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("엑셀 다운로드 실패:", error);
        } finally {
            setLoading(false);
        }
    };

    // -------------------------
    // ✅ CHART DATA (요구 반영)
    // -------------------------
    const totalBidCount = Number(stats?.totalBidCount || 0);
    const totalWinCount = Number(stats?.totalWinCount || 0);
    const lostCount = Math.max(0, totalBidCount - totalWinCount);

    const totalBidAmount = Number(stats?.totalBidAmount || 0);
    const totalWinAmount = Number(stats?.totalWinAmount || 0);
    const lostAmount = Math.max(0, totalBidAmount - totalWinAmount);

    const winRate = clamp(Number(stats?.winRate || 0), 0, 100);

    // 1) 투찰/낙찰(건수) -> 낙찰 vs 미낙찰 (도넛)
    const bidWinCountDonut = useMemo(
        () => [
            { name: "낙찰", value: totalWinCount },
            { name: "미낙찰", value: lostCount },
        ],
        [totalWinCount, lostCount]
    );

    // 2) 금액 -> 낙찰금액 vs 미낙찰금액(=총투찰-낙찰) (도넛)
    const bidWinAmountDonut = useMemo(
        () => [
            { name: "낙찰금액", value: totalWinAmount },
            { name: "미낙찰금액", value: lostAmount },
        ],
        [totalWinAmount, lostAmount]
    );

    // 3) 낙찰률 -> 낙찰률 vs 나머지 (도넛)
    const winRateDonut = useMemo(
        () => [
            { name: "낙찰률", value: winRate },
            { name: "기타", value: Math.max(0, 100 - winRate) },
        ],
        [winRate]
    );

    return (
        <>
            {/* 전역 로딩 */}
            <Backdrop open={loading} sx={{ zIndex: (t) => t.zIndex.drawer + 1, bgcolor: "rgba(255,255,255,0.85)" }}>
                <Stack spacing={1} alignItems="center">
                    <LoadingDots label="자료를 불러오고 있어요" />
                </Stack>
            </Backdrop>

            {/* ✅ 회색 배경 제거 */}
            <Box sx={ds.components.external.page}>
                <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
                    <Stack spacing={2.5}>
                        {/* 검색 */}
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                <SearchBar
                                    onSelect={(bn) => {
                                        window.location.href = `/search?type=${encodeURIComponent(
                                            "기업 검색"
                                        )}&bizNumber=${encodeURIComponent(bn)}`;
                                    }}
                                />
                            </CardContent>
                        </Card>

                        {/* 사업자 정보 1카드 */}
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                {company ? (
                                    <Stack spacing={0.5}>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Typography sx={ds.components.external.heroTitle}>
                                                {company.name}
                                            </Typography>
                                            {/* 기업 즐겨찾기 토글 — 사업자번호 키 */}
                                            <FavoriteStar
                                                checked={companyFav}
                                                onToggle={toggleCompanyFav}
                                                size="medium"
                                            />
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="body2" color="text.secondary">
                                                사업자번호: {company.businessNumber}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label="기업"
                                                sx={{ ...ds.components.external.chip, height: 22 }}
                                            />
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <LoadingDots label="회사 정보를 불러오고 있어요" compact sx={{ py: 0 }} />
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>

                        {/* 진행중 표시 */}
                        {progress && (
                            <Card sx={cardSx}>
                                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                    <Stack spacing={0.75}>
                                        <Typography sx={{ fontWeight: 900, fontSize: 16 }}>
                                            낙찰 데이터 수집 중... (페이지 이탈 후 다른 작업하고 오셔도 괜찮습니다)
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {progress.fetched}/{progress.total} 건 완료 (
                                            {formatPercent(progress.progressPercent || 0)})
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}

                        {/* 요약 + 3 도넛 차트 */}
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                <Stack spacing={1.5}>
                                    <Typography sx={sectionTitleSx}>요약</Typography>

                                    {/* 숫자 */}
                                    {stats ? (
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" },
                                                gap: 1.5,
                                            }}
                                        >
                                            <MiniStat title="총 투찰" value={formatCount(totalBidCount)} />
                                            <MiniStat title="총 투찰금액" value={formatWon(totalBidAmount)} />
                                            <MiniStat title="낙찰" value={formatCount(totalWinCount)} accent={COLORS.winCount} />
                                            <MiniStat title="낙찰금액" value={formatWon(totalWinAmount)} accent={COLORS.winAmount} />
                                            <MiniStat title="낙찰률" value={formatPercent(winRate)} accent={COLORS.winRate} />
                                        </Box>
                                    ) : !progress ? (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <LoadingDots label="통계 정보를 불러오고 있어요" compact sx={{ py: 0 }} />
                                        </Stack>
                                    ) : null}

                                    <Divider />

                                    {/* ✅ 3 Donuts */}
                                    {stats && (
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                                                gap: 2,
                                            }}
                                        >
                                            {/* 1) 투찰/낙찰(건수) */}
                                            <DonutCard
                                                title="투찰 / 낙찰 (건수)"
                                                subtitle="투찰 중 낙찰 건수 비중"
                                                mainLabel="낙찰 건수"
                                                mainValueText={formatCount(totalWinCount)}
                                                data={bidWinCountDonut}
                                                colors={[COLORS.winCount, COLORS.muted]}
                                                tooltipFormatter={(v, name) => [formatCount(v), name]}
                                            />

                                            {/* 2) 금액(투찰 중 낙찰액) */}
                                            <DonutCard
                                                title="총 투찰금액 중 낙찰금액"
                                                subtitle="총 투찰금액 대비 낙찰금액 비중"
                                                mainLabel="낙찰금액"
                                                mainValueText={formatWon(totalWinAmount)}
                                                data={bidWinAmountDonut}
                                                colors={[COLORS.winAmount, COLORS.muted]}
                                                tooltipFormatter={(v, name) => [formatWon(v), name]}
                                            />

                                            {/* 3) 낙찰률 */}
                                            <DonutCard
                                                title="낙찰률"
                                                subtitle="낙찰 비중(%)"
                                                mainLabel="낙찰률"
                                                mainValueText={formatPercent(winRate)}
                                                data={winRateDonut}
                                                colors={[COLORS.winRate, COLORS.muted]}
                                                tooltipFormatter={(v, name) => [formatPercent(v), name]}
                                            />
                                        </Box>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>

                        {/* 결과 목록 */}
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={1.5}
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    sx={{ mb: 1.5 }}
                                >
                                    <Box>
                                        <Typography sx={{ ...ds.typography.sectionTitle, fontSize: 18 }}>
                                            입찰 결과 목록
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            (낙찰 업체가 없는 건 낙찰 결과가 없는 것입니다)
                                        </Typography>
                                    </Box>

                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={showOnlyWon}
                                                    onChange={() => setShowOnlyWon((v) => !v)}
                                                />
                                            }
                                            label="낙찰된 공고만"
                                        />

                                        <ActionButton variant="download"
                                            onClick={handleDownload}
                                            disabled={!bizNumber || !stats?.allResults}
                                            label="결과 엑셀 다운로드" />
                                    </Stack>
                                </Stack>

                                <Divider sx={{ mb: 1.5 }} />

                                {stats?.allResults ? (
                                    <Box sx={{ width: "100%", overflowX: "auto" }}>
                                        <ResizableTable size="small">
                                            <TableHead>
                                                <TableRow sx={ds.components.tableHead}>
                                                    <TableCell sx={{ minWidth: 360, fontWeight: 900 }}>
                                                        <TableSortLabel
                                                            active={sortKey === "bidTitle"}
                                                            direction={sortAsc ? "asc" : "desc"}
                                                            onClick={() => handleSort("bidTitle")}
                                                        >
                                                            공고명
                                                        </TableSortLabel>
                                                    </TableCell>

                                                    <TableCell sx={{ minWidth: 120, fontWeight: 900 }}>
                                                        <TableSortLabel
                                                            active={sortKey === "bidOpenDate"}
                                                            direction={sortAsc ? "asc" : "desc"}
                                                            onClick={() => handleSort("bidOpenDate")}
                                                        >
                                                            개찰일
                                                        </TableSortLabel>
                                                    </TableCell>

                                                    <TableCell sx={{ minWidth: 200, fontWeight: 900 }}>
                                                        <TableSortLabel
                                                            active={sortKey === "winningCompanyName"}
                                                            direction={sortAsc ? "asc" : "desc"}
                                                            onClick={() => handleSort("winningCompanyName")}
                                                        >
                                                            낙찰업체
                                                        </TableSortLabel>
                                                    </TableCell>

                                                    <TableCell align="right" sx={{ minWidth: 160, fontWeight: 900 }}>
                                                        <TableSortLabel
                                                            active={sortKey === "winningPrice"}
                                                            direction={sortAsc ? "asc" : "desc"}
                                                            onClick={() => handleSort("winningPrice")}
                                                        >
                                                            낙찰금액
                                                        </TableSortLabel>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {displayResults.map((r, i) => (
                                                    <TableRow
                                                        key={i}
                                                        hover
                                                        sx={{
                                                            cursor: "pointer",
                                                            "&:hover": { bgcolor: ds.colors.primaryBg },
                                                        }}
                                                        onClick={() => setSelected(r)}
                                                    >
                                                        <TableCell>
                                                            <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
                                                                {r.bidTitle}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            {r.bidOpenDate ? r.bidOpenDate.substring(0, 10) : "-"}
                                                        </TableCell>
                                                        <TableCell>{r.winningCompanyName || "-"}</TableCell>
                                                        <TableCell align="right">
                                                            {r.winningPrice
                                                                ? `${Number(r.winningPrice).toLocaleString()}원`
                                                                : "[낙찰 금액을 제공하지 않습니다]"}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </ResizableTable>
                                    </Box>
                                ) : !progress ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <LoadingDots label="입찰 결과 목록을 불러오고 있어요" compact sx={{ py: 0 }} />
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <HourglassTopIcon fontSize="small" color="action" />
                                        <Typography variant="body2" color="text.secondary">
                                            작업 진행 중입니다...
                                        </Typography>
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>

                        {/* 상세 모달 */}
                        <BidDetailModal
                            bidNumber={selected?.bidNoticeNumber}
                            bidNoticeOrder={selected?.bidNoticeOrder}
                            isOpen={!!selected}
                            onClose={() => setSelected(null)}
                        />
                    </Stack>
                </Box>
            </Box>
        </>
    );
};

export default CompanySearchPage;
